import React, { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Avatar } from './UI'
import {
  LayoutDashboard, Map, BookOpen, Building2, BarChart3,
  Bell, Settings, LogOut, Menu, X, ChevronDown, Plus,
  Users, Shield, Megaphone, Heart, FileText
} from 'lucide-react'

export function Logo({ dark = false }) {
  return (
    <img src="/logo.png" alt="AdsLocate" style={{ height: 52, width: 'auto' }} />
  )
}

// ── Advertiser nav items ─────────────────────────────────────
const advertiserNav = [
  { to: '/dashboard', icon: <LayoutDashboard size={18} />, label: 'Overview' },
  { to: '/explore', icon: <Map size={18} />, label: 'Explore Map' },
  { to: '/bookings', icon: <BookOpen size={18} />, label: 'My Bookings' },
  { to: '/saved', icon: <Heart size={18} />, label: 'Saved Spaces' },
]

// ── Media Owner nav items ─────────────────────────────────────
const ownerNav = [
  { to: '/dashboard', icon: <LayoutDashboard size={18} />, label: 'Overview' },
  { to: '/inventory', icon: <Building2 size={18} />, label: 'My Inventory' },
  { to: '/bookings', icon: <BookOpen size={18} />, label: 'Bookings' },
  { to: '/analytics', icon: <BarChart3 size={18} />, label: 'Analytics' },
]

// ── Admin nav items ─────────────────────────────────────────
const adminNav = [
  { to: '/admin', icon: <Shield size={18} />, label: 'Admin Panel' },
  { to: '/admin/users', icon: <Users size={18} />, label: 'Users' },
  { to: '/admin/spaces', icon: <Building2 size={18} />, label: 'Ad Spaces' },
  { to: '/admin/bookings', icon: <BookOpen size={18} />, label: 'All Bookings' },
]

export function Sidebar({ mobileOpen, onMobileClose }) {
  const { profile, signOut, isMediaOwner, isAdvertiser, isAdmin } = useAuth()
  const navigate = useNavigate()

  const navItems = isAdmin ? adminNav : isMediaOwner ? ownerNav : advertiserNav

  async function handleSignOut() {
    await signOut()
    navigate('/')
  }

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          onClick={onMobileClose}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99, display: 'none' }}
          className="show-mobile-overlay"
        />
      )}

      <aside className={`sidebar ${mobileOpen ? 'open' : ''}`}>
        {/* Logo */}
        <div className="sidebar-logo">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Logo />
            <button onClick={onMobileClose} className="btn btn-ghost btn-icon" style={{ color: 'rgba(255,255,255,0.5)', display: 'none' }}>
              <X size={18} />
            </button>
          </div>
          <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(255,255,255,0.06)', borderRadius: 8, display: 'inline-block' }}>
            <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {isAdmin ? '🛡 Admin' : isMediaOwner ? '📢 Media Owner' : '🎯 Advertiser'}
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <div className="sidebar-section-label">Main</div>

          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/dashboard' || item.to === '/admin'}
              className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
              onClick={onMobileClose}
            >
              <span className="nav-icon">{item.icon}</span>
              {item.label}
              {item.badge && <span className="nav-badge">{item.badge}</span>}
            </NavLink>
          ))}

          <div className="sidebar-section-label" style={{ marginTop: 12 }}>Account</div>

          <NavLink to="/notifications" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`} onClick={onMobileClose}>
            <span className="nav-icon"><Bell size={18} /></span>
            Notifications
          </NavLink>
          <NavLink to="/settings" className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`} onClick={onMobileClose}>
            <span className="nav-icon"><Settings size={18} /></span>
            Settings
          </NavLink>

          {isMediaOwner && (
            <>
              <div className="sidebar-section-label" style={{ marginTop: 12 }}>Quick Actions</div>
              <button className="sidebar-nav-item" onClick={() => { navigate('/inventory/new'); onMobileClose?.() }}>
                <span className="nav-icon"><Plus size={18} /></span>
                Add New Space
              </button>
            </>
          )}
        </nav>

        {/* User footer */}
        <div className="sidebar-footer">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar name={profile?.full_name || profile?.email} size={36} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: 'white', fontWeight: 600, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {profile?.full_name || 'User'}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {profile?.email}
              </div>
            </div>
            <button onClick={handleSignOut} className="btn btn-ghost btn-icon" style={{ color: 'rgba(255,255,255,0.4)', flexShrink: 0 }} title="Sign out">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}

export function TopBar({ onMenuClick, title, actions }) {
  const { profile } = useAuth()
  const navigate = useNavigate()

  return (
    <header className="top-bar">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <button onClick={onMenuClick} className="btn btn-ghost btn-icon" style={{ display: 'none' }}>
          <Menu size={20} />
        </button>
        {title && (
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 700, color: 'var(--gray-900)' }}>
            {title}
          </h1>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {actions}
        <button onClick={() => navigate('/notifications')} className="btn btn-ghost btn-icon" style={{ position: 'relative' }}>
          <Bell size={18} />
        </button>
        <div
          onClick={() => navigate('/settings')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 8px', borderRadius: 8, transition: 'background 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-100)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <Avatar name={profile?.full_name || profile?.email} size={32} />
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)' }} className="hide-mobile">
            {profile?.full_name?.split(' ')[0] || 'Account'}
          </span>
          <ChevronDown size={14} style={{ color: 'var(--gray-400)' }} />
        </div>
      </div>
    </header>
  )
}

export function DashboardLayout({ children, title, actions }) {
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <div className="dashboard-layout">
      <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
      <main className="main-content">
        <TopBar onMenuClick={() => setMobileOpen(true)} title={title} actions={actions} />
        <div className="page-content animate-fadeIn">
          {children}
        </div>
      </main>
    </div>
  )
}
